import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SiteLogo from "./SiteLogo";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const catRef = useRef<HTMLDivElement>(null);

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled && !mobileOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setCatOpen(false);
    setMobileOpen(false);
    setMobileCatOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    navigate(`/search?q=${encodeURIComponent(kw)}`);
    setMobileOpen(false);
  };

  const linkBase = "text-sm transition-colors cursor-pointer";
  const linkColor = transparent
    ? "text-background-50/90 hover:text-background-50"
    : "text-foreground-700 hover:text-primary-600";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        transparent ? "bg-transparent" : "bg-background-50/95 backdrop-blur border-b border-background-200"
      }`}
    >
      <div className="w-full px-4 md:px-6 h-16 flex items-center gap-4">
        <SiteLogo variant={transparent ? "light" : "default"} />

        <nav className="hidden md:flex items-center gap-1 ml-4">
          <Link
            to="/"
            className={`${linkBase} ${linkColor} px-3 py-2 rounded-md ${
              location.pathname === "/" ? "font-medium" : ""
            }`}
          >
            首页
          </Link>

          <div className="relative" ref={catRef}>
            <button
              type="button"
              onClick={() => setCatOpen((v) => !v)}
              className={`${linkBase} ${linkColor} px-3 py-2 rounded-md flex items-center gap-1 whitespace-nowrap`}
            >
              分类
              <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? "rotate-180" : ""}`} aria-hidden="true"></i>
            </button>
            {catOpen && (
              <div className="absolute top-full left-0 mt-2 w-[560px] bg-background-50 rounded-lg border border-background-200 p-4 grid grid-cols-2 gap-x-6 gap-y-3 animate-fade-up">
                {categories.map((cat) => (
                  <div key={cat.id}>
                    <Link
                      to={`/category/${cat.id}`}
                      className="flex items-center gap-2 text-sm font-medium text-foreground-900 hover:text-primary-600 cursor-pointer"
                    >
                      <span className="flex items-center justify-center w-5 h-5 text-primary-600">
                        <i className={cat.icon} aria-hidden="true"></i>
                      </span>
                      {cat.name}
                    </Link>
                    <div className="mt-2 flex flex-wrap gap-1.5 pl-7">
                      {cat.subcategories.map((sub) => (
                        <Link
                          key={sub}
                          to={`/category/${cat.id}`}
                          className="text-xs text-secondary-600 hover:text-accent-600 bg-secondary-100/70 hover:bg-accent-100 rounded-full px-2 py-0.5 cursor-pointer"
                        >
                          {sub}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Link to="/about" className={`${linkBase} ${linkColor} px-3 py-2 rounded-md`}>
            关于我们
          </Link>
        </nav>

        <form onSubmit={submitSearch} className="hidden md:flex items-center ml-auto">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400 text-base" aria-hidden="true"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              type="text"
              placeholder="搜索软件名称、标签"
              className="w-52 lg:w-64 h-10 pl-9 pr-3 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-900 placeholder:text-secondary-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
            />
          </div>
          <button
            type="submit"
            className="ml-2 h-10 px-4 text-sm rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </form>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="打开菜单"
          className={`md:hidden ml-auto flex items-center justify-center w-10 h-10 rounded-md ${transparent ? "text-background-50" : "text-foreground-900"}`}
        >
          <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`} aria-hidden="true"></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4 space-y-3">
          <form onSubmit={submitSearch} className="flex items-center gap-2">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400" aria-hidden="true"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="text"
                placeholder="搜索软件名称、标签"
                className="w-full h-11 pl-9 pr-3 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-900 placeholder:text-secondary-400 focus:outline-none focus:border-primary-400"
              />
            </div>
            <button type="submit" className="h-11 px-4 text-sm rounded-md bg-primary-500 text-background-50 whitespace-nowrap cursor-pointer">
              搜索
            </button>
          </form>

          <Link to="/" className="block py-2.5 text-sm text-foreground-900 border-b border-background-200">
            首页
          </Link>

          <div className="border-b border-background-200">
            <button
              type="button"
              onClick={() => setMobileCatOpen((v) => !v)}
              className="w-full flex items-center justify-between py-2.5 text-sm text-foreground-900"
            >
              分类
              <i className={`ri-arrow-down-s-line transition-transform ${mobileCatOpen ? "rotate-180" : ""}`} aria-hidden="true"></i>
            </button>
            {mobileCatOpen && (
              <div className="pb-3 space-y-3">
                {categories.map((cat) => (
                  <div key={cat.id}>
                    <Link to={`/category/${cat.id}`} className="flex items-center gap-2 py-1 text-sm font-medium text-foreground-900">
                      <span className="flex items-center justify-center w-5 h-5 text-primary-600">
                        <i className={cat.icon} aria-hidden="true"></i>
                      </span>
                      {cat.name}
                    </Link>
                    <div className="flex flex-wrap gap-1.5 pl-7 mt-1">
                      {cat.subcategories.map((sub) => (
                        <Link
                          key={sub}
                          to={`/category/${cat.id}`}
                          className="text-xs text-secondary-600 bg-secondary-100 rounded-full px-2 py-0.5"
                        >
                          {sub}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Link to="/about" className="block py-2.5 text-sm text-foreground-900">
            关于我们
          </Link>
        </div>
      )}
    </header>
  );
}